import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, MessageCircle } from "lucide-react";
import MessageThread from "@/components/messages/MessageThread";
import api, { API } from "@/lib/api";

const POLL_INTERVAL_MS = 7000;

/** Dedicated "Messages" tab: conversation list (one per unlocked application) on the left,
 * selected thread on the right. Shared by both the producer and candidate dashboards. */
export default function MessagesTab() {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = () => {
    api.get("/messages/conversations").then(({ data }) => {
      setConversations(data);
      if (!selectedId && data.length > 0) setSelectedId(data[0].application_id);
    });
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (conversations === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7]" /></div>;

  if (conversations.length === 0) {
    return <p className="text-center py-16 text-[#2B2B2B]/50" data-testid="messages-tab-empty">{t("messages.noConversationsYet")}</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4" data-testid="messages-tab">
      <div className="space-y-1.5">
        {conversations.map((c) => (
          <button key={c.application_id} onClick={() => setSelectedId(c.application_id)}
                  className={`w-full text-left p-3 rounded-xl border transition-colors ${selectedId === c.application_id ? "bg-[#4ba3b7]/10 border-[#4ba3b7]/30" : "bg-white border-black/5 hover:bg-[#FAFAFA]"}`}
                  data-testid={`conversation-item-${c.application_id}`}>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-[#FAFAFA] border border-black/10 overflow-hidden flex items-center justify-center shrink-0">
                {c.counterpart.photo_path ? (
                  <img src={`${API}/profiles/files/${c.counterpart.photo_path}`} alt="" className="h-full w-full object-cover" />
                ) : (
                  <MessageCircle className="h-4 w-4 text-[#4ba3b7]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{c.counterpart.name || c.offer_title}</p>
                <p className="text-xs text-[#2B2B2B]/50 truncate">{c.last_message ? c.last_message.content : t("messages.noMessagesYet")}</p>
              </div>
              {c.unread_count > 0 && (
                <span className="h-5 min-w-5 px-1.5 rounded-full bg-[#4ba3b7] text-white text-[10px] font-bold flex items-center justify-center shrink-0" data-testid={`conversation-unread-badge-${c.application_id}`}>
                  {c.unread_count}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5">
        {selectedId && <MessageThread key={selectedId} applicationId={selectedId} />}
      </div>
    </div>
  );
}
